///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The interactive node for one webcam. The layer draws the whole catalog
/// from arrays (WebcamCloudNode); a WebcamNode exists only for the camera
/// under the pointer, the focused camera, and deep-link targets, where the
/// focus manager, hover callout and info panel need a real scene node. Its
/// dome is drawn exactly like the cloud's so the hand-off is invisible.
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { WebcamEntry } from "@shared-types/webcam"
import { type Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { trig } from "@stovokor/math"
import { animation, easing } from "@stovokor/math/animation"
import { rgba, type color } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { dot } from "@stovokor/math/vector"

export const cWebcamHeightAboveTerrainKm = 0.02    // 20m above terrain
// Physical dome size, which is what you see up close (far away the dome
// inflates to cWebcamMinAngular instead). Kept small: webcams are pole-top
// hardware, not ocean buoys, and traffic cams sit a few hundred meters apart.
export const cMarkerSphereRadius = 0.4       // km
// Pickable area = the marker's apparent size times this comfort factor.
// Kept tight because traffic cams can sit a few hundred meters apart.
export const cHitPadding = 2

const cWebcamFocusDistance = 25
const cWebcamFocusMaxZoomRadius = 80

// Ember dome marker matching the buoy/quake/satellite visual language.
// Clustering caps density, so individual markers can afford to be bright
// and clearly visible without dense traffic grids merging into blobs.
export const cWebcamMinAngular = 0.002
export const cWebcamFill = 0.8
export const cWebcamFarFill = 0.6

// Maximum apparent size (radians of screen-space radius). Without a ceiling,
// the 0.4 km physical dome renders ~25 px wide over a metro area. 0.0065 rad
// keeps markers at a discrete ~15 px regardless of camera distance.
export const cWebcamMaxAngular = 0.0065

// Category colors: fuchsia/pink camera family (see design-system/03-hud-canvas.md,
// "Webcam Category Palette"). The magenta hue range is unused by any other
// entity type, so cameras never read as buoys/ships/satellites. Traffic stays
// muted so a dense city grid reads as infrastructure rather than points of
// interest, and wildlife keeps green for the nature association.
export const cCategoryColors: Record<WebcamEntry["category"], color> = {
	traffic: rgba(0.82, 0.58, 0.88, 0.85),
	scenic: rgba(0.85, 0.27, 0.94, 0.9),
	weather: rgba(0.93, 0.28, 0.6, 0.9),
	marine: rgba(0.98, 0.44, 0.52, 0.9),
	aviation: rgba(0.78, 0.45, 1.0, 0.9),
	wildlife: rgba(0.45, 0.9, 0.45, 0.9),
}
export const cDefaultColor = rgba(0.85, 0.27, 0.94, 0.9)
export const cStaleColor = rgba(0.6, 0.6, 0.6, 0.6)

/** Brightened, solid variant of a marker color for the hovered camera */
export function hoverColor(base: color): color {
	return rgba(
		Math.min(1, base.r * 1.3 + 0.2),
		Math.min(1, base.g * 1.3 + 0.2),
		Math.min(1, base.b * 1.3 + 0.2),
		Math.min(1, base.a * 1.3)
	)
}

export class WebcamNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	public coordinate: geographic
	private cameraAdapter: EntityCameraAdapter

	public entry: WebcamEntry
	private ebContext: EarthBrowserContext
	private markerDome: EmberDomeNode
	private focusAnim?: animation
	private focusBlend = 0
	private elevationResolved = false
	private terrainVersion = -1
	private hovered = false
	private domeScale = cMarkerSphereRadius

	constructor(context: EarthBrowserContext, entry: WebcamEntry) {
		super(context)
		this.id = `webcam-${entry.id}`
		this.entry = entry
		this.ebContext = context
		this.coordinate = new geographic(entry.lon * trig.rad, entry.lat * trig.rad, cWebcamHeightAboveTerrainKm)
		this.applyTerrainElevation()

		this.markerDome = new EmberDomeNode(context)
		this.markerDome.fillAlpha = cWebcamFill
		this.markerDome.farFillAlpha = cWebcamFarFill
		this.markerDome.minAngularSize = cWebcamMinAngular
		this.markerDome.transform = matrix.scale(cMarkerSphereRadius)
		// Only traffic cameras are domes; every other camera is a thumbnail
		// chip that the cloud keeps drawing (reading this node's hover and
		// focus state), so the node contributes no marker of its own
		this.markerDome.visible = entry.category === "traffic"
		this.updateMarkerColor()
		this.addNode(this.markerDome)

		this.cameraAdapter = new EntityCameraAdapter(context, this, cWebcamFocusDistance, cWebcamFocusMaxZoomRadius)
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}

	/**
	 * Sit the marker on the terrain: ellipsoid + base elevation + a small
	 * offset. The elevation layer loads async at startup; until it's ready,
	 * getElevation returns 0 (sea level) and render() retries once it loads.
	 */
	private applyTerrainElevation(): void {
		this.terrainVersion = this.ebContext.terrainVersion
		const elevationKm = this.ebContext.displayElevationKm(this.coordinate)
		this.coordinate = new geographic(
			this.coordinate.longitude,
			this.coordinate.latitude,
			elevationKm + cWebcamHeightAboveTerrainKm
		)
		this.transform = matrix.translate(this.coordinate.cartesian)
		this.elevationResolved = this.ebContext.baseElevation.loaded
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.entry.name,
			subtitle: this.entry.region ?? undefined,
			theme: "webcam",
			contentType: "webcam",
			contentData: {
				type: "webcam",
				webcam: this.entry
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)

		if (this.entry.id) {
			pushTargetToHistory('webcam', this.entry.id)
		}

		this.animateFocusBlend(1)
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.animateFocusBlend(0)
	}

	/** Fade the dome down while focused so it doesn't sit on top of the panel anchor */
	private animateFocusBlend(target: number): void {
		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(target)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => {
				this.focusBlend = value
				this.markerDome.opacity = 1 - 0.9 * value
			})
		this.focusAnim.start()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	private updateMarkerColor(): void {
		const base = this.entry.status === "stale"
			? cStaleColor
			: cCategoryColors[this.entry.category] ?? cDefaultColor

		if (this.hovered) {
			// Brighten and solidify the dome so the user can see which camera
			// the tap would select before committing in a dense cluster
			this.markerDome.color = hoverColor(base)
			this.markerDome.fillAlpha = 1
		} else {
			this.markerDome.color = base
			this.markerDome.fillAlpha = cWebcamFill
		}
	}

	public updateFeature(entry: WebcamEntry): void {
		this.entry = entry
		this.updateMarkerColor()
	}

	/** Hover cue from EntityHoverHandler: highlight the would-be-picked dome */
	public onHoverHighlight(hovered: boolean): void {
		this.hovered = hovered
		this.updateMarkerColor()
	}

	public get isHovered(): boolean {
		return this.hovered
	}

	/** Marker opacity multiplier: faded down while focused so the marker doesn't sit on the panel anchor */
	public get markerOpacity(): number {
		return 1 - 0.9 * this.focusBlend
	}

	/** Focused, or still fading out of focus */
	public get isFocused(): boolean {
		return this.focusBlend > 0 || this.ebContext.focusManager.getFocusedNode() === this
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			return []
		}

		// Nodes built before the async elevation layer finished loading sit at
		// sea level; snap them onto the terrain as soon as data is available,
		// and re-seat when the terrain exaggeration changes
		if ((!this.elevationResolved || this.terrainVersion !== this.ebContext.terrainVersion) && this.ebContext.baseElevation.loaded) {
			this.applyTerrainElevation()
		}

		const distance = state.camera.position.subtract(this.transform.position).length

		// Apparent-size ceiling: shrink the physical dome once the camera is
		// close enough that 0.4 km would exceed cWebcamMaxAngular on screen.
		// (The shader-side minAngularSize floor still applies when far away.)
		const targetScale = Math.min(cMarkerSphereRadius, distance * cWebcamMaxAngular)
		if (Math.abs(targetScale - this.domeScale) > this.domeScale * 0.02) {
			this.domeScale = targetScale
			this.markerDome.transform = matrix.scale(targetScale)
		}

		return super.render(state)
	}

	/**
	 * Custom picking for dense clusters (city traffic cams sit a few hundred
	 * meters apart). Two differences from a plain hit-sphere test:
	 *
	 * 1. The pickable radius tracks the marker's apparent size — its enforced
	 *    minimum angular size when far away, its physical dome up close —
	 *    instead of a fixed radius that dwarfs the spacing between cameras.
	 * 2. The reported distance is biased by the ray's perpendicular miss from
	 *    the marker center. Overlapping neighbors sit at nearly the same ray
	 *    depth, so without the bias the recognizer's closest-first sort always
	 *    awards the tap to whichever sphere the ray happens to enter first;
	 *    with it, the camera whose center is nearest the cursor wins.
	 */
	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Chip cameras are picked by the cloud over the chip's rectangle,
		// which reports the hit on this node; a second, dome-sized hit here
		// would only duplicate it
		if (!this.markerDome.visible) {
			return undefined
		}

		// Closest approach of the pick ray to the marker center (local origin).
		// The node transform is a pure translation, so the local-space ray
		// direction is still unit length.
		const rayDepth = -dot(state.ray.origin, state.ray.direction)
		if (rayDepth <= 0) {
			return undefined
		}
		const missDistance = state.ray.pointAt(rayDepth).length

		// Same clamped size the marker renders at: physical dome capped by the
		// max-angular ceiling, floored by the far-mode min-angular dot
		const apparentRadius = Math.max(
			Math.min(cMarkerSphereRadius, rayDepth * cWebcamMaxAngular),
			rayDepth * cWebcamMinAngular
		)
		if (missDistance > apparentRadius * cHitPadding) {
			return undefined
		}

		return {
			node: this,
			hitPoint: state.ray.pointAt(rayDepth),
			distance: rayDepth + missDistance
		}
	}
}
